// src/features/tuition/components/scheduling/SessionForm.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Divider,
  FormControlLabel,
  Switch,
  Autocomplete,
  Chip,
  Avatar, 
  IconButton,
  CircularProgress,
  FormHelperText,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Input
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  PersonAdd as PersonAddIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  CloudUpload as UploadIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  Link as LinkIcon,
  VideoLibrary as VideoIcon,
  Description as DescriptionIcon,
  PlayCircleOutline as PlayIcon,
  MenuBook as MenuBookIcon,
  Topic as TopicIcon,
  SubdirectoryArrowRight as SubtopicIcon
} from '@mui/icons-material';
import { THEME_COLORS } from '../../../../styles/theme';
import { fetchStudents } from '../../services/studentsService';
import { createSession, updateSession } from '../../services/sessionsService';
import { useAuth } from '../../../../contexts/AuthContext';
import { collection, getDocs, query, where, orderBy, getDoc, doc } from 'firebase/firestore';
import { db, storage } from '../../../../firebase/config';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

const RECURRENCE_OPTIONS = [
  { value: 'none', label: 'Does not repeat' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Every two weeks' },
  { value: 'monthly', label: 'Monthly' }
];

const RESOURCE_TYPES = [
  { value: 'document', label: 'Document', icon: <FileIcon />, accept: '.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx' },
  { value: 'image', label: 'Image', icon: <ImageIcon />, accept: '.jpg,.jpeg,.png,.gif' },
  { value: 'video', label: 'Video', icon: <VideoIcon />, accept: '.mp4,.mov,.avi' },
  { value: 'link', label: 'Link', icon: <LinkIcon /> }
];

const SessionForm = ({ open, onClose, onSubmit, initialData = null, preselectedStudent = null }) => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [students, setStudents] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [curriculumItems, setCurriculumItems] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [resourceType, setResourceType] = useState('document');
  const [resourceFile, setResourceFile] = useState(null);
  const [resourceUrl, setResourceUrl] = useState('');
  const [resourceName, setResourceName] = useState('');
  const [isAddingResource, setIsAddingResource] = useState(false);
  
  const [formData, setFormData] = useState({
    students: [],
    subject: '',
    subjectId: '',
    curriculumItemId: '',
    topic: '',
    subtopic: '',
    date: new Date().toISOString().split('T')[0],
    startTime: '15:00',
    endTime: '16:00',
    duration: 60,
    location: '',
    notes: '',
    status: 'scheduled',
    recurring: false,
    recurringPattern: {
      frequency: 'weekly',
      daysOfWeek: [],
      endDate: new Date(new Date().setMonth(new Date().getMonth() + 1)).toISOString().split('T')[0]
    },
    resources: []
  });
  
  // Load subjects from Firestore
  useEffect(() => {
    const loadSubjects = async () => {
      if (!currentUser) return;
      
      try {
        const subjectsRef = collection(db, "subjects");
        const q = query(
          subjectsRef,
          where("createdBy", "==", currentUser.uid),
          orderBy("name")
        );
        
        const querySnapshot = await getDocs(q);
        const subjectList = [];
        
        querySnapshot.forEach((doc) => {
          subjectList.push({
            id: doc.id,
            ...doc.data()
          });
        });
        
        setSubjects(subjectList);
      } catch (error) {
        console.error("Error loading subjects:", error);
      }
    };
    
    loadSubjects();
  }, [currentUser]);
  
  // Load students from Firestore
  useEffect(() => {
    const loadStudents = async () => {
      if (!currentUser) return;
      
      setDataLoading(true);
      try {
        const studentsData = await fetchStudents(currentUser.uid);
        // Filter to only include active students
        const activeStudents = studentsData.filter(student => student.status === 'active');
        setStudents(activeStudents);
      } catch (error) {
        console.error("Error loading students:", error);
      } finally {
        setDataLoading(false);
      }
    };
    
    loadStudents();
  }, [currentUser]);
  
  // Load curriculum items when subject changes
  useEffect(() => {
    if (!formData.subjectId) {
      setCurriculumItems([]);
      return;
    }
    
    // Find the selected subject and get its curriculum items
    const selectedSubject = subjects.find(s => s.id === formData.subjectId);
    
    if (selectedSubject && selectedSubject.curriculumItems) {
      setCurriculumItems(selectedSubject.curriculumItems || []);
    } else {
      setCurriculumItems([]);
    }
  }, [formData.subjectId, subjects]);
  
  // Initialize form with initial data or preselected student
  useEffect(() => {
    if (initialData) {
      setFormData({
        ...initialData,
        // Ensure date formats are correct
        date: initialData.date || new Date().toISOString().split('T')[0],
        recurring: Boolean(initialData.recurring),
        recurringPattern: initialData.recurringPattern || {
          frequency: 'weekly',
          daysOfWeek: [],
          endDate: new Date(new Date().setMonth(new Date().getMonth() + 1)).toISOString().split('T')[0]
        },
        resources: initialData.resources || []
      });
    } else {
      // Reset form data
      const defaultForm = {
        students: [],
        subject: '',
        subjectId: '',
        curriculumItemId: '',
        topic: '',
        subtopic: '',
        date: new Date().toISOString().split('T')[0],
        startTime: '15:00',
        endTime: '16:00',
        duration: 60,
        location: '',
        notes: '',
        status: 'scheduled',
        recurring: false,
        recurringPattern: {
          frequency: 'weekly',
          daysOfWeek: [],
          endDate: new Date(new Date().setMonth(new Date().getMonth() + 1)).toISOString().split('T')[0]
        },
        resources: []
      };
      
      // If a student is preselected (from StudentDetail view)
      if (preselectedStudent) {
        defaultForm.students = [preselectedStudent];
      }
      
      setFormData(defaultForm);
    }
  }, [initialData, preselectedStudent, open]);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'startTime' || name === 'endTime') {
      // Recalculate duration when time changes
      const updatedFormData = { ...formData, [name]: value };
      const durationMinutes = calculateDurationInMinutes(updatedFormData.startTime, updatedFormData.endTime);
      updatedFormData.duration = durationMinutes;
      setFormData(updatedFormData);
    } else if (name === 'duration') {
      // Recalculate end time when duration changes
      const updatedFormData = { ...formData, duration: parseInt(value, 10) || 0 };
      const endTime = calculateEndTime(updatedFormData.startTime, updatedFormData.duration);
      updatedFormData.endTime = endTime;
      setFormData(updatedFormData);
    } else if (name.startsWith('recurringPattern.')) {
      // Handle nested recurringPattern object
      const field = name.split('.')[1];
      setFormData({
        ...formData,
        recurringPattern: {
          ...formData.recurringPattern,
          [field]: value
        }
      });
    } else if (name === 'subject') {
      // When subject changes, find the subject ID and reset curriculum item
      const selectedSubject = subjects.find(s => s.name === value);
      setFormData({
        ...formData,
        subject: value,
        subjectId: selectedSubject?.id || '',
        curriculumItemId: '', // Reset curriculum item
        topic: '',
        subtopic: ''
      });
    } else if (name === 'curriculumItemId') {
      // When curriculum item changes, update topic and subtopic
      const selectedItem = curriculumItems.find(item => item.id === value);
      
      if (selectedItem) {
        setFormData({
          ...formData,
          curriculumItemId: value,
          topic: selectedItem.topic || '',
          subtopic: selectedItem.subtopic || ''
        });
      } else {
        setFormData({
          ...formData,
          curriculumItemId: value,
          topic: '',
          subtopic: ''
        });
      }
    } else {
      // Regular field updates
      setFormData({ ...formData, [name]: value });
    }
    
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };
  
  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setFormData({ ...formData, [name]: checked });
  };
  
  const handleStudentsChange = (event, newValue) => {
    setFormData({ ...formData, students: newValue });
    
    // Clear error when user selects students
    if (errors.students) {
      setErrors(prev => ({ ...prev, students: '' }));
    }
  };
  
  const handleDayOfWeekToggle = (day) => {
    const currentDays = [...formData.recurringPattern.daysOfWeek];
    const index = currentDays.indexOf(day);
    
    if (index === -1) {
      currentDays.push(day);
    } else {
      currentDays.splice(index, 1);
    }
    
    setFormData({
      ...formData,
      recurringPattern: {
        ...formData.recurringPattern,
        daysOfWeek: currentDays
      }
    });
  };
  
  const calculateDurationInMinutes = (start, end) => {
    if (!start || !end) return 60;
    
    const [startHours, startMinutes] = start.split(':').map(Number);
    const [endHours, endMinutes] = end.split(':').map(Number);
    
    const startDate = new Date();
    startDate.setHours(startHours, startMinutes, 0, 0);
    
    const endDate = new Date();
    endDate.setHours(endHours, endMinutes, 0, 0);
    
    // If end time is earlier than start time, assume it's the next day
    if (endDate < startDate) {
      endDate.setDate(endDate.getDate() + 1);
    }
    
    // Calculate difference in minutes
    return Math.round((endDate - startDate) / (1000 * 60));
  };
  
  const calculateEndTime = (start, durationMinutes) => {
    if (!start || !durationMinutes) return '';
    
    const [hours, minutes] = start.split(':').map(Number);
    
    const startDate = new Date();
    startDate.setHours(hours, minutes, 0, 0);
    
    const endDate = new Date(startDate.getTime() + durationMinutes * 60000);
    
    // Format to HH:MM
    const endHours = endDate.getHours().toString().padStart(2, '0');
    const endMinutes = endDate.getMinutes().toString().padStart(2, '0');
    
    return `${endHours}:${endMinutes}`;
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (formData.students.length === 0) {
      newErrors.students = 'At least one student is required';
    }
    
    if (!formData.subject) {
      newErrors.subject = 'Subject is required';
    }
    
    if (!formData.date) {
      newErrors.date = 'Date is required';
    }
    
    if (!formData.startTime) {
      newErrors.startTime = 'Start time is required';
    }
    
    if (!formData.endTime) {
      newErrors.endTime = 'End time is required';
    }
    
    if (formData.recurring && 
        formData.recurringPattern.frequency !== 'none' && 
        formData.recurringPattern.frequency !== 'daily' && 
        formData.recurringPattern.daysOfWeek.length === 0) {
      newErrors.daysOfWeek = 'Select at least one day of the week';
    }
    
    // Validate resource fields if currently adding a resource
    if (isAddingResource) {
      if (!resourceName.trim()) {
        newErrors.resourceName = 'Resource name is required';
      }
      
      if (resourceType === 'link' && !resourceUrl.trim()) {
        newErrors.resourceUrl = 'URL is required';
      } else if (resourceType !== 'link' && !resourceFile) {
        newErrors.resourceFile = 'Please select a file to upload';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setResourceFile(e.target.files[0]);
    }
  };
  
  const handleAddResource = async () => {
    if (!resourceName.trim() || (resourceType === 'link' && !resourceUrl.trim()) || (resourceType !== 'link' && !resourceFile)) {
      validateForm();
      return;
    }
    
    setLoading(true);
    
    try {
      let resource = {
        name: resourceName,
        type: resourceType,
        createdAt: new Date().toISOString()
      };
      
      if (resourceType === 'link') {
        resource.url = resourceUrl;
      } else {
        // Upload file
        const storageRef = ref(storage, `sessions/${currentUser.uid}/${Date.now()}_${resourceFile.name}`);
        await uploadBytes(storageRef, resourceFile);
        const downloadUrl = await getDownloadURL(storageRef);
        
        resource.url = downloadUrl;
        resource.fileName = resourceFile.name;
        resource.storagePath = storageRef.fullPath;
      }
      
      // Add resource to formData
      const updatedResources = [...formData.resources, resource];
      setFormData({ ...formData, resources: updatedResources });
      
      // Reset resource form
      setResourceName('');
      setResourceFile(null);
      setResourceUrl('');
      setIsAddingResource(false);
    } catch (error) {
      console.error('Error uploading resource:', error);
      setErrors(prev => ({ ...prev, resourceUpload: 'Failed to upload resource: ' + error.message }));
    } finally {
      setLoading(false);
    }
  };
  
  const handleRemoveResource = async (index) => {
    const resource = formData.resources[index];
    
    setLoading(true);
    
    try {
      // If it's a file (not a link), delete from storage
      if (resource.type !== 'link' && resource.storagePath) {
        const fileRef = ref(storage, resource.storagePath);
        await deleteObject(fileRef).catch(err => {
          console.warn('Storage object may not exist:', err);
        });
      }
      
      // Remove from form data
      const updatedResources = [...formData.resources];
      updatedResources.splice(index, 1);
      setFormData({ ...formData, resources: updatedResources });
    } catch (error) {
      console.error('Error removing resource:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      // Prepare data for submission
      const sessionData = {
        ...formData,
        // Create array of studentIds and names for easier querying, with validation
        studentIds: formData.students.map(s => s?.id || '').filter(id => id),
        studentNames: formData.students.map(s => {
          if (!s) return '';
          const firstName = s.firstName || '';
          const lastName = s.lastName || '';
          return `${firstName} ${lastName}`.trim();
        }).filter(name => name),
        tutorId: currentUser.uid,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      if (initialData && initialData.id) {
        // Update existing session
        await updateSession(initialData.id, sessionData);
      } else {
        // Create new session
        await createSession(sessionData);
      }
      
      if (onSubmit) {
        onSubmit(sessionData);
      }
      
      onClose();
    } catch (error) {
      console.error("Error saving session:", error);
    } finally {
      setLoading(false);
    }
  };
  
  // Get day of week for the selected date
  const getDayOfWeek = (dateString) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const date = new Date(dateString);
    return days[date.getDay()];
  };
  
  // Get file type icon based on resource type
  const getResourceIcon = (resource) => {
    switch (resource.type) {
      case 'document':
        return <DescriptionIcon />;
      case 'image':
        return <ImageIcon />;
      case 'video':
        return <VideoIcon />;
      case 'link':
        return <LinkIcon />;
      default:
        return <FileIcon />;
    }
  };
  
  return (
    <Dialog
      open={open}
      onClose={loading ? null : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight="bold">
            {initialData ? 'Edit Session' : 'Schedule New Session'}
          </Typography>
          <IconButton 
            edge="end" 
            color="inherit" 
            onClick={onClose}
            disabled={loading}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Student Selection */}
          <Grid item xs={12}>
            <FormControl fullWidth error={!!errors.students}>
              <Autocomplete
                multiple
                id="students-select"
                options={students}
                loading={dataLoading}
                value={formData.students}
                onChange={handleStudentsChange}
                getOptionLabel={(option) => {
                  // Handle null or undefined option
                  if (!option) return '';
                  
                  const firstName = option.firstName || '';
                  const lastName = option.lastName || '';
                  const grade = option.grade || '';
                  
                  return `${firstName} ${lastName}${grade ? ` (Grade ${grade})` : ''}`.trim();
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Students"
                    placeholder="Select students for this session"
                    required
                    error={!!errors.students}
                    helperText={errors.students}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {dataLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    // Make sure the option is valid and has firstName property
                    const firstName = option?.firstName || '';
                    const lastName = option?.lastName || '';
                    const initial = firstName ? firstName.charAt(0) : '?';
                    
                    return (
                      <Chip
                        avatar={
                          <Avatar sx={{ bgcolor: THEME_COLORS.primary }}>
                            {initial}
                          </Avatar>
                        }
                        label={`${firstName} ${lastName}`.trim() || 'Unknown Student'}
                        {...getTagProps({ index })}
                        sx={{ backgroundColor: 'rgba(39, 35, 97, 0.08)' }}
                      />
                    );
                  })
                }
                disabled={loading}
              />
            </FormControl>
          </Grid>
          
          {/* Subject Selection */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={!!errors.subject}>
              <InputLabel required>Subject</InputLabel>
              <Select
                name="subject"
                value={formData.subject}
                label="Subject*"
                onChange={handleInputChange}
                disabled={loading}
              >
                <MenuItem value="">
                  <em>Select a subject</em>
                </MenuItem>
                {subjects.map((subject) => (
                  <MenuItem key={subject.id} value={subject.name}>
                    {subject.name} - Grade {subject.grade}
                  </MenuItem>
                ))}
              </Select>
              {errors.subject && <FormHelperText>{errors.subject}</FormHelperText>}
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Location"
              name="location"
              value={formData.location}
              onChange={handleInputChange}
              placeholder="e.g., Online, Library, Home"
              disabled={loading}
            />
          </Grid>
          
          {/* Curriculum item selection */}
          {formData.subject && (
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Curriculum Item</InputLabel>
                <Select
                  name="curriculumItemId"
                  value={formData.curriculumItemId}
                  label="Curriculum Item"
                  onChange={handleInputChange}
                  disabled={loading || curriculumItems.length === 0}
                >
                  <MenuItem value="">
                    <em>Select a curriculum item</em>
                  </MenuItem>
                  {curriculumItems.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {item.topic} - {item.subtopic}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.term ? `Term ${item.term}` : ''} 
                          {item.startDate && item.finishDate ? 
                            ` (${item.startDate} to ${item.finishDate})` : ''}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {curriculumItems.length === 0 && formData.subject && (
                  <FormHelperText>
                    No curriculum items available for this subject
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
          )}
          
          {/* Selected topic and subtopic display */}
          {formData.topic && (
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'rgba(39, 35, 97, 0.04)' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Topic:
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {formData.topic}
                    </Typography>
                  </Grid>
                  
                  {formData.subtopic && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Subtopic:
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {formData.subtopic}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            </Grid>
          )}
          
          {/* Date & Time */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Date & Time
            </Typography>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              required
              label="Date"
              name="date"
              type="date"
              value={formData.date}
              onChange={handleInputChange}
              InputLabelProps={{ shrink: true }}
              error={!!errors.date}
              helperText={errors.date || getDayOfWeek(formData.date)}
              disabled={loading}
            />
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              required
              label="Start Time"
              name="startTime"
              type="time"
              value={formData.startTime}
              onChange={handleInputChange}
              InputLabelProps={{ shrink: true }}
              error={!!errors.startTime}
              helperText={errors.startTime}
              disabled={loading}
            />
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              required
              label="End Time"
              name="endTime"
              type="time"
              value={formData.endTime}
              onChange={handleInputChange}
              InputLabelProps={{ shrink: true }}
              error={!!errors.endTime}
              helperText={`Duration: ${formData.duration} minutes`}
              disabled={loading}
            />
          </Grid>
          
          {/* Recurring Options */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.recurring}
                  onChange={handleSwitchChange}
                  name="recurring"
                  color="primary"
                />
              }
              label="Recurring Session"
              disabled={loading}
            />
          </Grid>
          
          {formData.recurring && (
            <>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Frequency</InputLabel>
                  <Select
                    name="recurringPattern.frequency"
                    value={formData.recurringPattern.frequency}
                    label="Frequency"
                    onChange={handleInputChange}
                    disabled={loading}
                  >
                    {RECURRENCE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="End Date"
                  name="recurringPattern.endDate"
                  type="date"
                  value={formData.recurringPattern.endDate}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                  disabled={loading}
                />
              </Grid>
              
              {formData.recurringPattern.frequency !== 'none' && 
               formData.recurringPattern.frequency !== 'daily' && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Repeat on:
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
                      <Chip
                        key={day}
                        label={day.substring(0, 3)}
                        onClick={() => handleDayOfWeekToggle(day)}
                        color={formData.recurringPattern.daysOfWeek.includes(day) ? "primary" : "default"}
                        sx={{
                          bgcolor: formData.recurringPattern.daysOfWeek.includes(day) 
                            ? THEME_COLORS.primary 
                            : 'rgba(0, 0, 0, 0.08)',
                          color: formData.recurringPattern.daysOfWeek.includes(day) 
                            ? 'white' 
                            : 'text.primary',
                          '&:hover': {
                            bgcolor: formData.recurringPattern.daysOfWeek.includes(day) 
                              ? '#1e1c4e' 
                              : 'rgba(0, 0, 0, 0.12)'
                          }
                        }}
                        disabled={loading}
                      />
                    ))}
                  </Box>
                  {errors.daysOfWeek && (
                    <FormHelperText error>{errors.daysOfWeek}</FormHelperText>
                  )}
                </Grid>
              )}
            </>
          )}
          
          {/* Notes */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              multiline
              rows={3}
              placeholder="Add any notes about this session..."
              disabled={loading}
            />
          </Grid>
          
          {/* Resources section (only for editing sessions) */}
          {initialData && (
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle1" fontWeight="medium">
                  Session Resources
                </Typography>
                
                {!isAddingResource && (
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => setIsAddingResource(true)}
                    sx={{
                      borderColor: THEME_COLORS.primary,
                      color: THEME_COLORS.primary,
                      '&:hover': {
                        borderColor: '#1e1c4e',
                        backgroundColor: 'rgba(39, 35, 97, 0.04)'
                      }
                    }}
                    disabled={loading}
                  >
                    Add Resource
                  </Button>
                )}
              </Box>
              
              {/* Add resource section */}
              {isAddingResource && (
                <Paper sx={{ p: 2, mb: 3, bgcolor: 'rgba(39, 35, 97, 0.04)' }}>
                  <Typography variant="subtitle2" fontWeight="medium" mb={2}>
                    Add New Resource
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Resource Name"
                        value={resourceName}
                        onChange={(e) => setResourceName(e.target.value)}
                        required
                        error={!!errors.resourceName}
                        helperText={errors.resourceName}
                        disabled={loading}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Resource Type</InputLabel>
                        <Select
                          value={resourceType}
                          onChange={(e) => setResourceType(e.target.value)}
                          label="Resource Type"
                          disabled={loading}
                        >
                          {RESOURCE_TYPES.map((type) => (
                            <MenuItem key={type.value} value={type.value}>
                              <Box display="flex" alignItems="center">
                                {type.icon}
                                <Typography sx={{ ml: 1 }}>{type.label}</Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      {resourceType === 'link' ? (
                        <TextField
                          fullWidth
                          label="URL"
                          value={resourceUrl}
                          onChange={(e) => setResourceUrl(e.target.value)}
                          placeholder="https://"
                          required
                          error={!!errors.resourceUrl}
                          helperText={errors.resourceUrl}
                          disabled={loading}
                        />
                      ) : (
                        <Box>
                          <input
                            accept={RESOURCE_TYPES.find(t => t.value === resourceType)?.accept || '*'}
                            style={{ display: 'none' }}
                            id="resource-file-upload"
                            type="file"
                            onChange={handleFileChange}
                            disabled={loading}
                          />
                          <label htmlFor="resource-file-upload">
                            <Button
                              variant="outlined"
                              component="span"
                              startIcon={<UploadIcon />}
                              sx={{
                                borderColor: THEME_COLORS.primary,
                                color: THEME_COLORS.primary,
                                '&:hover': {
                                  borderColor: '#1e1c4e',
                                  backgroundColor: 'rgba(39, 35, 97, 0.04)'
                                }
                              }}
                              disabled={loading}
                            >
                              Choose File
                            </Button>
                          </label>
                          {resourceFile && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              Selected: {resourceFile.name}
                            </Typography>
                          )}
                          {errors.resourceFile && (
                            <FormHelperText error>{errors.resourceFile}</FormHelperText>
                          )}
                        </Box>
                      )}
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Box display="flex" justifyContent="flex-end" gap={1}>
                        <Button
                          onClick={() => {
                            setIsAddingResource(false);
                            setResourceName('');
                            setResourceFile(null);
                            setResourceUrl('');
                          }}
                          disabled={loading}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="contained"
                          onClick={handleAddResource}
                          disabled={loading || (!resourceFile && resourceType !== 'link') || (resourceType === 'link' && !resourceUrl.trim()) || !resourceName.trim()}
                          sx={{
                            bgcolor: THEME_COLORS.primary,
                            '&:hover': { bgcolor: '#1e1c4e' }
                          }}
                        >
                          Add Resource
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                  
                  {errors.resourceUpload && (
                    <Box mt={2}>
                      <Typography color="error" variant="body2">
                        {errors.resourceUpload}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              )}
              
              {/* List of existing resources */}
              {formData.resources.length > 0 ? (
                <Grid container spacing={2}>
                  {formData.resources.map((resource, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent sx={{ pb: 1 }}>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                            <Typography variant="subtitle2" fontWeight="medium" noWrap>
                              {resource.name}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveResource(index)}
                              disabled={loading}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                          
                          <Box display="flex" alignItems="center" mt={1}>
                            {getResourceIcon(resource)}
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                              {resource.type.charAt(0).toUpperCase() + resource.type.slice(1)}
                            </Typography>
                          </Box>
                          
                          {resource.fileName && (
                            <Typography variant="body2" color="text.secondary" noWrap sx={{ mt: 0.5 }}>
                              {resource.fileName}
                            </Typography>
                          )}
                        </CardContent>
                        <CardActions>
                          <Button
                            size="small"
                            component="a"
                            href={resource.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            startIcon={resource.type === 'video' ? <PlayIcon /> : <LinkIcon />}
                            sx={{ color: THEME_COLORS.primary }}
                          >
                            {resource.type === 'video' ? 'Play' : resource.type === 'link' ? 'Visit' : 'Open'}
                          </Button>
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box py={3} textAlign="center" bgcolor="rgba(0, 0, 0, 0.02)" borderRadius={1}>
                  <Typography color="text.secondary">
                    No resources added yet
                  </Typography>
                  {!isAddingResource && (
                    <Button
                      variant="text"
                      onClick={() => setIsAddingResource(true)}
                      startIcon={<AddIcon />}
                      sx={{ mt: 1, color: THEME_COLORS.primary }}
                    >
                      Add your first resource
                    </Button>
                  )}
                </Box>
              )}
            </Grid>
          )}
        </Grid>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button 
          onClick={onClose}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          startIcon={loading && <CircularProgress size={20} color="inherit" />}
          sx={{
            bgcolor: THEME_COLORS.primary,
            '&:hover': { bgcolor: '#1e1c4e' }
          }}
        >
          {loading ? 'Saving...' : initialData ? 'Update Session' : 'Schedule Session'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionForm;